import React from 'react';
import {useTranslation} from 'react-i18next';
import FacebookLogin from '@greatsumini/react-facebook-login';

import {facebookAppId} from '../../../settings/settings';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {FACEBOOK_CHANNEL, GOOGLE_CHANNEL} from '../../channels/channels';
import {CheckCircle, Warning} from '../../icons';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {clearOauthReturn} from '../oauthReturn';
import {FACEBOOK_SDK_NOT_LOADED} from '../connectEvents';
import {CLARITY_UNMASK} from '../../../utils/clarity/unmask';

/**
 * El copy de cada motivo de fallo que el backend sabe nombrar.
 *
 * Es un mapa y no el mensaje del backend a propósito: ese texto puede nombrar a otra agencia, así
 * que lo que viaja es un CÓDIGO y la traducción vive acá. Mismo patrón que el paso 3 con los
 * errores de `add-bulk`.
 *
 * `GOOGLE_NO_ADS_ACCESS` es el caso más frecuente del paso y el que peor se contaba: la persona
 * autoriza con la cuenta de Google con la que entra al mail, esa cuenta no llega a ninguna cuenta
 * de Google Ads, y la tarjeta decía «suele ser un permiso que falta» — un permiso que nadie tiene
 * que dar, y que manda a pedirle accesos al cliente en vez de reintentar con la cuenta correcta.
 */
const ERROR_REASON_KEYS = {
    GOOGLE_NO_ADS_ACCESS: 'onboarding.platforms.error_no_ads_access',
    NETWORK_ERROR: 'onboarding.platforms.error_network',
    // El SDK de Meta no cargó y el diálogo nunca se abrió (UD-23, 02/11): hasta acá se decía «quedó sin
    // completar», como si la persona hubiera cancelado.
    [FACEBOOK_SDK_NOT_LOADED]: 'onboarding.platforms.error_meta_not_loaded',
};

/**
 * Una plataforma, en sus cuatro estados: sin conectar, descubriendo, conectada y fallida.
 *
 * El fallo se cuenta acá adentro y no en un cartel arriba: es de ESTA plataforma, el resto del
 * recorrido sigue en pie, y desde la misma tarjeta se puede reintentar o seguir sin ella. Un error
 * de OAuth no es el final del camino.
 *
 * Y desde el 10/09 dice POR QUÉ cuando el backend lo sabe. La premisa vieja —«casi siempre es un
 * permiso que falta»— era falsa justo en el caso más frecuente: la cuenta de Google con la que la
 * persona entra al mail no llega a ninguna cuenta de Google Ads, y no hay permiso que dar.
 */
export function PlatformCard({channel, hintKey, connected, syncing, stalled, accountCount, accountsKnown,
                              failed, failedReason, cancelled, onConnect, onRetry, onDismissError,
                              onCancelled, onUnavailable, onTap}) {
    const {t} = useTranslation();
    const name = t(`onboarding.platforms.names.${channel}`);
    const reasonKey = ERROR_REASON_KEYS[failedReason];
    /**
     * Conectada, el padrón ya llegó, y no trae una sola cuenta.
     *
     * Es el MISMO defecto que `platformsResult.js` arregló para la sección del paso, sobrevivido una
     * superficie más adentro: la tarjeta decía «Conectada · 0 cuentas» con el tilde verde de éxito,
     * o sea afirmaba un hallazgo sobre cero. Se ve en producción con LinkedIn, que casi ninguna
     * agencia usa y casi toda conecta.
     *
     * `accountsKnown` es el guard que hace honesta la frase: `accountCount` sale del padrón, que
     * llega mucho después que las conexiones, así que sin él la tarjeta anunciaría «sin cuentas
     * publicitarias» durante los segundos en que todavía no sabemos nada.
     */
    const connectedButEmpty = connected && accountsKnown && accountCount === 0;

    return (
        <article className={cardClassName({connected, failed, syncing, connectedButEmpty})}>
            <header className="ob-platform__head">
                <span className="ob-platform__logo">
                    <ChannelLogo channel={channel} hspace={0} vspace={0} marginLeft={0} padding={0}/>
                </span>
                <span className="ob-platform__name" {...CLARITY_UNMASK}>{name}</span>
            </header>

            {hintKey && !connected && !failed && !cancelled && (
                <p className="ob-platform__hint" {...CLARITY_UNMASK}>{t(`onboarding.platforms.hints.${hintKey}`)}</p>
            )}

            <footer className="ob-platform__foot">
                {/* Cerrar el diálogo del proveedor NO es un error, y decir «suele ser un permiso que
                    falta» sobre una decisión deliberada manda al usuario a buscar un problema que no
                    existe. Pero tampoco puede no pasar nada: hasta acá, cancelar en Meta dejaba la
                    tarjeta exactamente igual que antes de tocarla, sin ninguna señal de que el
                    intento terminó. Mismo par de acciones que el error, otra explicación. */}
                {cancelled && !failed && (
                    <div className="ob-platform__error -soft" role="status">
                        <Warning size={15}/>
                        <p>{t('onboarding.platforms.cancelled', {platform: name,
                                                                 interpolation: {escapeValue: false}})}</p>
                        <div className="ob-platform__error-actions">
                            <button type="button" className="ob-btn -mini -primary" onClick={onRetry} {...CLARITY_UNMASK}>
                                {t('onboarding.platforms.retry')}
                            </button>
                            <button type="button" className="ob-btn -mini -ghost" onClick={onDismissError}>
                                {t('onboarding.platforms.skip', {platform: name,
                                                                 interpolation: {escapeValue: false}})}
                            </button>
                        </div>
                    </div>
                )}
                {failed && (
                    <div className="ob-platform__error" role="alert">
                        <Warning size={15}/>
                        <p>{reasonKey
                            ? t(reasonKey, {platform: name, interpolation: {escapeValue: false}})
                            : t('onboarding.platforms.error', {platform: name,
                                                               interpolation: {escapeValue: false}})}</p>
                        {/* La llave de acceso va en el estado de ERROR y no en el hint de la
                            tarjeta: el hint sólo se ve ANTES de conectar y desaparece justo cuando
                            falla. Cualquier agencia con Workspace endurecido se cruza esa pantalla
                            de Google —le pasó a Pablo el 07/09— y se lee como una falla nuestra;
                            no lo es, y decirlo acá es lo único que lo aclara en el momento.

                            Sólo cuando NO sabemos el motivo, y eso se decidió viendo las dos
                            tarjetas juntas: con el motivo identificado quedan dos explicaciones
                            distintas para el mismo fallo, y la segunda manda a mirar donde no es —
                            si supimos que la cuenta no tiene Google Ads, el consentimiento se
                            completó y la llave de acceso quedó atrás. La pista sirve justamente
                            cuando la tarjeta sólo puede decir «suele ser un permiso que falta». */}
                        {channel === GOOGLE_CHANNEL && !reasonKey && (
                            <p className="ob-platform__error-note">
                                {t('onboarding.platforms.error_passkey_note')}
                            </p>
                        )}
                        <div className="ob-platform__error-actions">
                            <button type="button" className="ob-btn -mini -primary" onClick={onRetry} {...CLARITY_UNMASK}>
                                {t('onboarding.platforms.retry')}
                            </button>
                            <button type="button" className="ob-btn -mini -ghost" onClick={onDismissError}>
                                {t('onboarding.platforms.skip', {platform: name,
                                                                 interpolation: {escapeValue: false}})}
                            </button>
                        </div>
                    </div>
                )}

                {!failed && syncing && (
                    <div className="ob-platform__busy" aria-live="polite">
                        <span className="ob-spinner-mini"/>
                        <span>{t('onboarding.platforms.counting')}</span>
                    </div>
                )}

                {/* Se tardó más de lo razonable: se dice, en vez de dejar el spinner prometiendo
                    que está por pasar algo. Las cuentas siguen apareciendo solas más tarde. */}
                {!failed && stalled && (
                    <div className="ob-platform__busy" aria-live="polite">
                        <span>{t('onboarding.platforms.taking_long')}</span>
                    </div>
                )}

                {/* La confirmación de la vuelta del OAuth es ESTA línea, y no un aviso aparte.
                    Había un `ob-platform__flash` alimentado por `just_connected` de la URL: sin
                    texto, sin CSS y —por su propia condición `!syncing`— visible sólo cuando la
                    tarjeta ya dice «Conectada · N cuentas» con su tilde verde. Era un segundo
                    anuncio de lo mismo, así que se fue con su estado. Lo que sí faltaba es que el
                    cambio se ANUNCIE al pasar de «Descubriendo…» a conectada. */}
                {/* Conectada pero el padrón todavía no llegó: no se sabe cuántas cuentas hay, así
                    que no se dice ningún número. Sin esta rama, `accountCount` vale 0 por no saber y
                    la tarjeta anunciaba «Conectada · 0 cuentas» CON el tilde verde — el mismo bug
                    que `connectedButEmpty` vino a matar, sobreviviendo en la ventana en que el guard
                    lo apaga. Y no es un instante: `candidates` también queda nulo si `loadCandidates`
                    falla, y el padrón es la consulta cara del paso. */}
                {!failed && !syncing && !stalled && connected && !accountsKnown && (
                    <div className="ob-platform__busy" aria-live="polite">
                        <span className="ob-spinner-mini"/>
                        <span>{t('onboarding.platforms.counting')}</span>
                    </div>
                )}

                {!failed && !syncing && !stalled && connected && accountsKnown && (
                    connectedButEmpty ? (
                        // Sin tilde verde: el permiso se dio, pero no hay nada que monitorear. El
                        // ícono de éxito acá es lo que hacía leer un cero como un hallazgo. El
                        // padding reemplaza el lugar que ocupaba el ícono: sin él, este texto
                        // arranca 24 px a la izquierda de sus tres hermanas de la grilla.
                        <div className="ob-platform__done -empty" aria-live="polite">
                            <span>{t('onboarding.platforms.connected_none')}</span>
                        </div>
                    ) : (
                        <div className="ob-platform__done" aria-live="polite">
                            <CheckCircle size={16} weight="fill"/>
                            <span>{t('onboarding.platforms.connected', {count: accountCount})}</span>
                        </div>
                    )
                )}

                {!failed && !cancelled && !syncing && !stalled && !connected && (
                    <ConnectButton channel={channel} label={t('onboarding.platforms.connect')}
                                   onConnect={onConnect} onCancelled={onCancelled} onUnavailable={onUnavailable}
                                   onTap={onTap}/>
                )}
            </footer>
        </article>
    );
}

/** El paso al que Meta tiene que devolver al usuario: el único desde el que esta tarjeta se monta. */
const PASO_DE_PLATAFORMAS = '/onboarding/plataformas';

/**
 * Meta se autoriza con su propio diálogo y las demás navegando fuera de la aplicación.
 *
 * Se usa el mismo componente de siempre para Meta en vez de replicar el flujo: cambia el botón, no
 * la conexión — y ese flujo tiene resuelto el detalle de forzar la elección de cuenta, sin el cual
 * no se puede conectar una segunda.
 *
 * **Meta no usa la nota de retorno, y no es un olvido.** La nota existe para cruzar una navegación
 * de página entera: los otros tres canales se van del sitio y vuelven a una pantalla de callback que
 * tiene que averiguar a dónde ir. Meta autoriza en un popup y nunca sale de `/onboarding/plataformas`,
 * así que el destino se conoce acá mismo, al escribir el código.
 *
 * Hasta el 22/09 esto intentaba dejar la nota igual, con un `onClick` sobre `<FacebookLogin>` — y
 * ese componente **no acepta esa prop**: desestructura una lista fija y renderiza su botón sin
 * spread, con su propio handler. El `onClick` se descartaba en silencio, la nota nunca se escribía y
 * el éxito terminaba en la pantalla clásica de conexiones, fuera del recorrido (card dJxYEHZ9). El
 * camino de error no lo sufría porque ya escribía su destino a mano, y esa asimetría —el fallo
 * volvía al túnel, el éxito no— era lo que hacía el bug difícil de leer.
 */
function ConnectButton({channel, label, onConnect, onCancelled, onUnavailable, onTap}) {
    const {t} = useTranslation();

    if (channel !== FACEBOOK_CHANNEL) {
        return (
            <button type="button" className="ob-btn -ghost" onClick={onConnect} {...CLARITY_UNMASK}>{label}</button>
        );
    }

    // El toque de Meta se cuenta acá (UD-23): `<FacebookLogin>` descarta un `onClick` (ver arriba), así
    // que se escucha en captura desde un envoltorio que no ocupa lugar (`display: contents`).
    return (
        <span className="ob-connect-wrap" onClickCapture={onTap}>
            <FacebookLogin
                appId={facebookAppId}
                scope="ads_management,ads_read,business_management"
                loginOptions={{auth_type: 'reauthenticate'}}
                onSuccess={response => {
                    const credentialLabel = localStorage.getItem('pending_credential_label');
                    localStorage.removeItem('pending_credential_label');
                    new APIClient(API_URL + 'store_facebook_access_token/')
                        .call('POST', {...response, credential_label: credentialLabel})
                        .then(data => {
                            // Puede haber una nota viva de OTRO canal: si el usuario empezó a conectar
                            // Google —que sí la escribe— y volvió sin completar, dura quince minutos. Se
                            // limpia acá porque estas dos ramas ya no la consumen, y porque navegar con
                            // `window.location` recarga la página entera: el desmontaje de
                            // `OnboardingLayout`, que es el otro lugar que la borra, nunca llega a correr.
                            clearOauthReturn();
                            window.location = data?.status === 'OK'
                                ? `${PASO_DE_PLATAFORMAS}?just_connected=${FACEBOOK_CHANNEL}`
                                : `${PASO_DE_PLATAFORMAS}?oauth_error=${FACEBOOK_CHANNEL}`;
                        });
                }}
                onFail={result => {
                    // Cancelar el diálogo es una decisión del usuario y no una falla, pero algo tiene
                    // que pasar: la tarjeta quedaba idéntica a antes de tocarla y no había forma de
                    // saber si el intento había salido. Los otros tres canales sí avisan (vuelven con
                    // `oauth_error`); éste era el único mudo.
                    //
                    // La nota de retorno se limpia acá porque este camino no la consume: Meta autoriza
                    // sin salir de la página, así que nadie más la iba a gastar.
                    clearOauthReturn();
                    // `facebookNotLoaded` no es cancelar: el SDK no cargó (un bloqueador o la red lenta)
                    // y el diálogo nunca se abrió. Va como error, con su propio texto y su evento.
                    if (result?.status === 'facebookNotLoaded' && onUnavailable) {
                        onUnavailable();
                        return;
                    }
                    if (onCancelled) onCancelled();
                }}
                className="ob-btn -ghost"
            >
                <span {...CLARITY_UNMASK}>{t('onboarding.platforms.connect')}</span>
            </FacebookLogin>
        </span>
    );
}

/**
 * El marco verde es una AFIRMACION, igual que el de la seccion del paso.
 *
 * `platformsResult.js` ya razona esto para el bloque de arriba —«solo lo lleva el estado que de
 * verdad encontro cuentas»— y la tarjeta se lo habia quedado: a LinkedIn se le saco el tilde y
 * conservo el borde y el fondo verdes, identicos a los de Google, Meta y TikTok. El mismo defecto
 * una capa mas afuera que el que arreglo `connectedButEmpty`.
 */
function cardClassName({connected, failed, syncing, connectedButEmpty}) {
    const classes = ['ob-platform'];
    if (failed) classes.push('-error');
    else if (connected && !syncing && !connectedButEmpty) classes.push('-on');
    return classes.join(' ');
}
